/**
 * Purpose: Main chat playground component that displays the active chat conversation
 *
 * How it works:
 * 1. Displays the chat header, pinned messages, and message list
 * 2. Conditionally renders the RTF input based on user permissions and chat state
 * 3. Controls input visibility based on thread settings and user role
 * 4. Shows attachment viewer dialog for media interactions
 *
 * Key features:
 * - Role-based input visibility (canPost permission)
 * - Hides input when viewing threads (activeThreadId is set)
 * - Hides input for removed members
 * - Responsive layout with gradient effects
 * - Integration with message virtualization
 */

import { useNavigationState } from "@/zustand/NavigationState";
import { useCallState } from "@/zustand/CallState";
import { CHAT_HUB_ID } from "@/config/constants";
import { InitChatStatus } from "@/indexedDB/types";

import ChatPlaygroundHeader from "./ChatPlaygroundHeader";
import MessageList from "./messages/MessageList";
import RtfInput from "./rtfInput/RtfInput";
import MessageListLoading from "./messages/MessageListLoading";
import BottomSpacer from "@/components/shared/BottomSpacer";

import PinnedMessageList from "./pinnedMessages/PinnedMessageList";
import { ChatType } from "@/server/types/chat";
import { useDragDrop } from "./rtfInput/hooks/useDragDrop";
import DragDropOverlay from "./DragDropOverlay";

export default function ChatPlayground() {
  const activeChatRemovedRoleAsMember = useNavigationState(
    (state) => state.activeChatRemovedRoleAsMember,
  );

  const activeChatHubId = useNavigationState((state) => state.activeChatHubId);
  const activeChatType = useNavigationState((state) => state.activeChatType);

  const activeChatCanPost = useNavigationState(
    (state) => state.activeChatCanPost,
  );

  const activeThreadId = useNavigationState((state) => state.activeThreadId);

  const activeChatRoleAsMember = useNavigationState(
    (state) => state.activeChatRoleAsMember,
  );

  const activeChatInitStatus = useNavigationState(
    (state) => state.activeChatInitStatus,
  );

  // The in-call Orion chat panel (rendered inside the `Call` overlay, which is a sibling of this
  // middle column and stays mounted during a call) hosts its OWN `RtfInput` bound to the same active
  // chat. Suppress this background composer while that panel is open so there's a SINGLE live typing
  // emitter + draft writer for the call chat — otherwise the two composers double-bind (duplicate
  // typing emission, shared draft slot). The panel is only visible while the call is un-minimized,
  // which fully covers this playground, so hiding this composer has no UX cost.
  const isOrionChatPanelOpen = useCallState(
    (state) => state.isOrionChatPanelOpen,
  );

  const isChatWithArchitect =
    activeChatType === ChatType.GROUP && activeChatHubId === CHAT_HUB_ID;

  const { isDragging, dragHandlers } = useDragDrop();

  return (
    <div
      className="absolute inset-0 flex size-full flex-col overflow-hidden bg-card lg:border-l lg:border-border"
      {...dragHandlers}
    >
      <DragDropOverlay isDragging={isDragging} />
      <ChatPlaygroundHeader />
      <PinnedMessageList />
      <div
        id="MessageListContainer"
        className="relative flex-1 overflow-hidden"
      >
        {/* top gradient background stuff */}

        {isChatWithArchitect ? (
          <>
            <div
              data-testid="architect-hub-glow"
              className="pointer-events-none absolute left-1/2 top-[10%] h-[30rem] w-full -translate-x-1/2 bg-[radial-gradient(circle_at_center,_hsl(var(--gradient-color)/0.6),_transparent_70%)] opacity-60 blur-3xl lg:-top-28 lg:h-96 lg:w-1/2"
            />
            <div className="architect-glow-grain pointer-events-none absolute left-1/2 top-[10%] h-[30rem] w-full -translate-x-1/2 lg:-top-28 lg:h-96 lg:w-1/2" />
          </>
        ) : (
          <>
            <div className="pointer-events-none absolute -left-32 -top-28 hidden size-96 bg-[radial-gradient(circle_at_top_left,_hsl(var(--gradient-color)/0.5),_transparent)] opacity-50 blur-3xl lg:block"></div>
            <div className="pointer-events-none absolute -right-32 -top-28 hidden size-96 bg-[radial-gradient(circle_at_top_right,_hsl(var(--gradient-color)/0.5),_transparent)] opacity-50 blur-3xl lg:block"></div>
          </>
        )}

        <MessageListLoading>
          <MessageList />
        </MessageListLoading>
      </div>
      <div className="min-h-middleColumnFooterHeight flex w-full flex-col px-4 py-2">
        <div className="flex w-full flex-col space-y-2 lg:mx-auto lg:max-w-[1000px]">
          {/* TO DO => improve logic and handle deleted members */}
          {/* Suppress this composer while the in-call Orion chat panel owns the composer for the
              same chat (see `isOrionChatPanelOpen` note above) — keeps a single typing emitter/draft. */}
          {isOrionChatPanelOpen ||
          activeChatRemovedRoleAsMember ||
          activeChatInitStatus === InitChatStatus.PENDING ||
          activeThreadId ||
          (!activeThreadId &&
            activeChatRoleAsMember > activeChatCanPost) ? null : (
            <RtfInput />
          )}
        </div>

        <BottomSpacer />
      </div>
    </div>
  );
}
